import {DisplayListener, Payment, PaymentTerminal} from "@variocube/app-api";
import {
    CancelPayment,
    ConfirmPayment,
    DisplayRequest,
    InitiatePayment,
    PaymentApproved,
    PaymentDenied,
    PaymentTerminalMessageTypes,
} from "@variocube/driver-common";
import {VcmpClient} from "@variocube/vcmp";
import {CurrentPayment, InitiatePaymentError} from "./payment";



export class ControllerPaymentTerminal implements PaymentTerminal {
    private client: VcmpClient;

    private currentPayment?: CurrentPayment;

    constructor(url: string) {
        this.client = new VcmpClient(url);
        this.client.on(PaymentTerminalMessageTypes.DisplayRequest, this.handleDisplayRequest);
        this.client.on(PaymentTerminalMessageTypes.PaymentApproved, this.handlePaymentApproved);
        this.client.on(PaymentTerminalMessageTypes.PaymentDenied, this.handlePaymentDenied);
        this.client.start();
    }

    private readonly handleDisplayRequest = (displayRequest: DisplayRequest) => {
        this.currentPayment && this.currentPayment.displayListener(displayRequest.message);
    };

    private readonly handlePaymentApproved = (paymentApproved: PaymentApproved) => {
        this.currentPayment && this.currentPayment.resolve(paymentApproved);
    };

    private readonly handlePaymentDenied = (paymentDenied: PaymentDenied) => {
        this.currentPayment && this.currentPayment.reject(new InitiatePaymentError(paymentDenied.reason, false));
        this.currentPayment = undefined;
    };

    async initiatePayment(amount: number, currency: string, language: string, displayListener: DisplayListener) {
        if (!this.client.connected) {
            throw new InitiatePaymentError("Not connected to payment terminal service.", true);
        }

        if (this.currentPayment) {
            throw new InitiatePaymentError("There is already a payment in progress", true);
        }

        return new Promise<Payment>(async (resolve, reject) => {
            this.currentPayment = {
                amount, currency, displayListener, resolve, reject
            };

            try {
                await this.client.send<InitiatePayment>({
                    "@type": PaymentTerminalMessageTypes.InitiatePayment,
                    amount,
                    currency,
                    language
                });
            } catch (error) {
                console.error("Could not initiate payment", error);
                this.currentPayment = undefined;
                reject(new InitiatePaymentError(error, true));
            }
        });
    }

    async confirmPayment() {
        try {
            if (this.currentPayment) {
                await this.client.send<ConfirmPayment>({
                    "@type": PaymentTerminalMessageTypes.ConfirmPayment,
                });
            }
        }
        finally {
            this.currentPayment = undefined;
        }
    }

    async cancelPayment() {
        try {
            if (this.currentPayment) {
                this.currentPayment = undefined;
                await this.client.send<CancelPayment>({
                    "@type": PaymentTerminalMessageTypes.CancelPayment,
                });
            }
        }
        finally {
            this.currentPayment = undefined;
        }
    }

}

